
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

interface Equipment {
  id: string;
  name: string;
  base_price: number;
  custom_status?: string;
}

interface EquipmentDialogProps {
  equipment: Equipment | null;
  open: boolean;
  onClose: (dataChanged: boolean) => void;
}

export const EquipmentDialog = ({ equipment, open, onClose }: EquipmentDialogProps) => {
  const [equipmentName, setEquipmentName] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    if (equipment) {
      setEquipmentName(equipment.name || "");
      setBasePrice(equipment.base_price?.toString() || "");
    } else {
      setEquipmentName("");
      setBasePrice("");
    }
  }, [equipment]);
  
  const handleSave = async () => {
    try {
      if (!equipmentName) {
        toast({
          title: "Validation Error",
          description: "Please enter a name for the equipment",
          variant: "destructive",
        });
        return;
      }
      
      const price = parseFloat(basePrice);
      if (isNaN(price) || price < 0) {
        toast({
          title: "Validation Error",
          description: "Please enter a valid price for the equipment",
          variant: "destructive",
        });
        return;
      }
      
      setIsSubmitting(true);
      
      if (equipment) {
        // Update existing equipment
        const { error } = await supabase
          .from('equipment_types')
          .update({
            name: equipmentName,
            base_price: price
          })
          .eq('id', equipment.id);
          
        if (error) throw error;
        
        toast({
          title: "Equipment updated",
          description: "Equipment has been successfully updated",
        });
      } else {
        // Create new equipment
        // Get current user's ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        
        const { error } = await supabase
          .from('equipment_types')
          .insert({
            name: equipmentName,
            base_price: price,
            admin_id: user.id
          });
          
        if (error) throw error;
        
        toast({
          title: "Equipment added",
          description: "New equipment has been successfully added",
        });
      }
      
      onClose(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to save equipment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {equipment ? "Edit Equipment" : "Add New Equipment"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Equipment Name</Label>
            <Input
              id="name"
              value={equipmentName}
              onChange={(e) => setEquipmentName(e.target.value)}
              placeholder="Enter equipment name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="price">Unit Cost (€)</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              min="0"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              placeholder="Enter unit cost"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {equipment ? "Update" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
